/**
 * Parses a user message text that the backend has embedded with a context_quote
 * preamble and extracts the original quote and user message separately.
 *
 * KOS format:
 *   The user is referring to this excerpt from a previous response:
 *   "<quote>"
 *
 *   User's question: <message>
 *
 * RFPOS format:
 *   Regarding this excerpt from a previous response:
 *   "<quote>"
 *
 *   <message>
 */
export function parseQuotedMessage(text: string): {
  contextQuote: string | null
  userMessage: string
} {
  if (!text) return { contextQuote: null, userMessage: text }

  // KOS: "The user is referring to this excerpt from a previous response:\n"<quote>"\n\nUser's question: <message>"
  const kosMatch = text.match(
    /^The user is referring to this excerpt from a previous response:\s*"([\s\S]+?)"\s*\n\nUser's question:\s*([\s\S]+)$/
  )
  if (kosMatch) {
    return { contextQuote: kosMatch[1].trim(), userMessage: kosMatch[2].trim() }
  }

  // RFPOS: "Regarding this excerpt from a previous response:\n"<quote>"\n\n<message>"
  const rfposMatch = text.match(
    /^Regarding this excerpt from a previous response:\s*"([\s\S]+?)"\s*\n\n([\s\S]+)$/
  )
  if (rfposMatch) {
    return {
      contextQuote: rfposMatch[1].trim(),
      userMessage: rfposMatch[2].trim()
    }
  }

  return { contextQuote: null, userMessage: text }
}

/**
 * Strip the `(internal account id: <digits>)` disambiguator that the FE
 * injects into the API query in place of the literal "selected account"
 * phrase (see agent-search-v2.tsx#onSubmit). The injection is planner
 * plumbing — the user never typed it — but the BE persists the rewritten
 * form into agent_session_chat_history.user_input.text, so on reload the
 * user bubble would otherwise read "...for ABM Industries (internal
 * account id: 11337)". Strip it back out at render time so the bubble
 * reads clean: "...for ABM Industries".
 *
 * Match is global + case-insensitive; covers any account name preceding
 * the parenthetical. Whitespace before the paren is consumed too so we
 * don't leave a dangling space.
 */
export function stripInternalAccountId(text: string): string {
  if (!text) return text
  return text.replace(/\s*\(internal account id:\s*\d+\)/gi, "")
}
